import { useDebouncedValue } from "@mantine/hooks";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { DragDropContext, DropResult } from "react-beautiful-dnd";
import { useParams, useSearchParams } from "react-router-dom";

import {
  SourceOrDestination,
  useUpdateIssueStatus,
} from "api/issues/updateIssueStatus";
import { useGetProject } from "api/projects/getProject";
import MainHeading from "components/MainHeading";
import NotFound from "components/NotFound";
import { useUser } from "hooks/useUser";
import { Issue, Project } from "types";

import IssueModal from "../IssueModal";
import IssuesFilter from "../IssuesFilter";
import KanbanCard from "../KanbanCard";
import PageSkeleton from "../PageSkeleton";
import styles from "./KanbanBoard.module.css";
import {
  Avatar,
  Badge,
  Box,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { Calendar, Checkbox } from "tabler-icons-react";
import { getInitials } from "utils/getInitials";
import { capitalizeFirstLetter } from "utils/capitalizeFirstLetter";

const typeColorMap: Record<string, string> = {
  task: "#4fade6",
  story: "#65ba43",
  bug: "#e44d42",
};

export function KanbanBoard() {
  const { projectId } = useParams();
  const setSearchParams = useSearchParams()[1];

  const { user } = useUser();

  const { data, isLoading, isError, isSuccess } = useGetProject(
    projectId,
    user._id
  );

  const [project, setProject] = useState<Project>(
    data || {
      _id: "",
      name: "",
      members: [],
      description: "",
      category: "business",
      todoIssues: [],
      inReviewIssues: [],
      inProgressIssues: [],
      completedIssues: [],
    }
  );
  const [isProjectLoaded, setIsProjectLoaded] = useState(false);
  const [value, setValue] = useState("grid");

  const updateIssueStatusMutation = useUpdateIssueStatus(project?._id || "");

  const [titleFilter, setTitleFilter] = useState("");
  const [debouncedTitleFilter] = useDebouncedValue(titleFilter, 350);
  const [filteredAssignees, setFilteredAssignees] = useState<string[]>([]);
  useEffect(() => {
    if (isSuccess) {
      setProject(data);
      setIsProjectLoaded(true);
    }
  }, [data, isSuccess]);

  useLayoutEffect(() => {
    setTitleFilter("");
    setFilteredAssignees([]);
  }, [projectId]);
  useEffect(() => {
    const saved = localStorage.getItem("kanbanView");
    if (saved === "grid" || saved === "list") {
      setValue(saved);
    }
  }, []);
  useEffect(() => {
    localStorage.setItem("kanbanView", value);
  }, [value]);

  const filterIssues = (issues: Issue[]) => {
    if (titleFilter.length === 0 && filteredAssignees.length === 0) {
      return issues;
    }

    let filteredIssues = [...issues];

    if (filteredAssignees.length > 0) {
      filteredIssues = filteredIssues.filter((issue) => {
        if (!issue.assignee) {
          return false;
        }
        return filteredAssignees.includes(issue.assignee);
      });
    }

    if (debouncedTitleFilter.length > 0) {
      filteredIssues = filteredIssues.filter((issue) =>
        issue.title
          .toLowerCase()
          .includes(debouncedTitleFilter.toLowerCase().trim())
      );
    }

    return filteredIssues;
  };

  const onDragEnd = (result: DropResult) => {
    const { draggableId: issueId, source, destination } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    updateIssueStatusMutation.mutate({
      issueId,
      source: source.droppableId as SourceOrDestination,
      sourceIndex: source.index,
      destination: destination.droppableId as SourceOrDestination,
      destinationIndex: destination.index,
    });

    setProject((prev) => {
      const copiedSourceIssues = [
        ...prev[source.droppableId as SourceOrDestination],
      ];
      const updatedIssue = copiedSourceIssues[source.index];

      if (source.droppableId === destination.droppableId) {
        copiedSourceIssues.splice(source.index, 1);
        copiedSourceIssues.splice(destination.index, 0, updatedIssue);
        return {
          ...prev,
          [source.droppableId]: copiedSourceIssues,
        };
      }

      const copiedDestinationIssues = [
        ...prev[destination.droppableId as SourceOrDestination],
      ];
      copiedSourceIssues.splice(source.index, 1);
      copiedDestinationIssues.splice(destination.index, 0, updatedIssue);
      return {
        ...prev,
        [source.droppableId]: copiedSourceIssues,
        [destination.droppableId]: copiedDestinationIssues,
      };
    });
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case "done":
        return "#D1FAE5";
      case "in progress":
        return "#DBEAFE";
      case "in review":
        return "#FEF3C7";
      case "to do":
        return "";
      default:
        return "";
    }
  };
  const allTasks = [
    ...project.todoIssues,
    ...project.inReviewIssues,
    ...project.inProgressIssues,
    ...project.completedIssues,
  ];

  // Sort by creation date ascending (oldest to newest)
  const sortedTasks = allTasks.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  if (isLoading) {
    return <PageSkeleton />;
  }

  if (isError) {
    return <NotFound />;
  }

  if (isSuccess && !isProjectLoaded) {
    return <PageSkeleton />;
  }

  return (
    <>
      <MainHeading title={project.name} />
      <IssuesFilter
        titleFilter={titleFilter}
        setTitleFilter={setTitleFilter}
        filteredAssignees={filteredAssignees}
        setFilteredAssignees={setFilteredAssignees}
        members={project.members}
        setValue={setValue}
        value={value}
      />
      {value === "grid" ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className={styles.container}>
            <IssueModal
              members={project.members}
              orgId={user.org?._id}
              projectId={projectId || ""}
            />
            <KanbanCard
              title="TO DO"
              issues={filterIssues(project.todoIssues)}
              id="todoIssues"
              projectId={projectId}
              userId={user._id}
              members={project.members}
              orgId={user.org?._id}
              isDemoUser={user.isDemo || false}
            />
            <KanbanCard
              title="IN PROGRESS"
              issues={filterIssues(project.inProgressIssues)}
              id="inProgressIssues"
              projectId={projectId}
              userId={user._id}
              members={project.members}
              orgId={user.org?._id}
              isDemoUser={user.isDemo || false}
            />
            <KanbanCard
              title="IN REVIEW"
              issues={filterIssues(project.inReviewIssues)}
              id="inReviewIssues"
              projectId={projectId}
              userId={user._id}
              members={project.members}
              orgId={user.org?._id}
              isDemoUser={user.isDemo || false}
            />
            <KanbanCard
              title="DONE"
              issues={filterIssues(project.completedIssues)}
              id="completedIssues"
              projectId={projectId}
              userId={user._id}
              members={project.members}
              orgId={user.org?._id}
              isDemoUser={user.isDemo || false}
            />
          </div>
        </DragDropContext>
      ) : (
        <Box>
          <IssueModal
            members={project.members}
            orgId={user.org?._id}
            projectId={projectId || ""}
          />
          <TextInput
            placeholder="Type to create a new task ..."
            icon={<Checkbox />}
            disabled
            mb="md"
          />

          <ScrollArea style={{ height: "calc(100vh - 260px)" }} mb="md">
            <Stack spacing="xs">
              {filterIssues(sortedTasks).map((task) => (
                <Paper
                  key={task.title}
                  withBorder
                  p="xs"
                  radius="md"
                  style={{
                    backgroundColor: getStatusColor(task.status),
                  }}
                >
                  <Group position="apart">
                    <Group>
                      <Checkbox />
                      <Text
                        style={{ cursor: "pointer" }}
                        size="sm"
                        lineClamp={1}
                        onClick={() => {
                          setSearchParams({ selectedIssue: task._id });
                        }}
                      >
                        {task.title}
                      </Text>
                      <Group>
                        <Badge color="red" variant="light">
                          {capitalizeFirstLetter(task.priority)}
                        </Badge>
                      </Group>
                      <Group>
                        <Badge
                          variant="light"
                          style={{
                            backgroundColor:
                              typeColorMap[task.type] || "#e0e0e0",
                            color: "#fff",
                          }}
                        >
                          {capitalizeFirstLetter(task.type)}
                        </Badge>
                      </Group>
                    </Group>

                    <Group spacing="xs">
                      <Group spacing={4}>
                        <Calendar size={16} />
                        <Text size="xs">
                          {new Date(
                            task.dueDate ?? Date.now()
                          ).toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                          })}
                        </Text>
                      </Group>
                      {project.members
                        .filter((item) =>
                          [task.assignee, task.reporter].includes(item._id)
                        )
                        .map((u1) => (
                          <Avatar radius="xl" size="sm" key={u1._id}>
                            {getInitials(u1.firstName, u1.lastName)}
                          </Avatar>
                        ))}
                      <Badge color="green" variant="light">
                        {task.status}
                      </Badge>
                    </Group>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </ScrollArea>
        </Box>
      )}
    </>
  );
}
